import {
  CAlert,
  CButton,
  CCloseButton,
  CCol,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CLoadingButton,
} from '@coreui/react-pro'
import ReactQuill from 'react-quill'
import React, { useEffect, useRef, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import TeamService from 'src/service/TeamService'
import ToastComponent from 'src/components/common/TaostComponent'
import ImageDropzone, {
  MAX_IMAGE_SIZE,
  SUPPORTED_IMAGE_FORMATS,
} from 'src/components/common/ImageDropzone'

const EditForm = ({ teamId, visible, onClose, gradeList, fetchUsers }) => {
  const API_URL = process.env.REACT_APP_API_URL
  const [teamDetail, setTeamDetail] = useState()
  const [gradeName, setGradeName] = useState('')
  const [ownerDetail, setOwnerDetail] = useState('')
  const [errorMessages, setErrorMessages] = useState([])
  const [loader, setLoader] = useState(false)
  const quillRef = useRef()

  const existingLogoUrl = teamDetail?.image
    ? `${API_URL}uploads/team_sponsor/${teamDetail.image}`
    : teamDetail?.game_logo
    ? `${API_URL}uploads/game_logo/${teamDetail.game_logo}`
    : null

  useEffect(() => {
    if (visible && teamId) {
      TeamService.getTeamDetail(teamId).then((res) => {
        if (res.status === 200) {
          setTeamDetail(res.data)
          setGradeName(res.data.grade_name)
          setOwnerDetail(res?.data.owner_detail || '')
        }
      })
    }
  }, [visible, teamId])

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Team name is required').max(50, '50 Character Limit is allowed.'),
    image: Yup.mixed()
      .nullable()
      .test('fileFormat', 'Invalid file format, only JPG, PNG or WEBP is allowed', (value) =>
        value ? SUPPORTED_IMAGE_FORMATS.includes(value.type) : true,
      )
      .test('fileSize', 'File size too large, max file size is 2 MB', (value) =>
        value ? value.size <= MAX_IMAGE_SIZE : true,
      ),
  })

  const formik = useFormik({
    initialValues: {
      name: teamDetail?.name,
      team_owner: teamDetail?.team_owner,
      sponsor_link: teamDetail?.sponsor_link,
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      const formData = new FormData()
      formData.append('teamId', teamId)
      formData.append('name', data.name)
      formData.append('grade_name', gradeName)
      formData.append('sponsor_link', data.sponsor_link)
      formData.append('team_owner', data.team_owner)
      formData.append('owner_detail', ownerDetail)
      if (data.image) {
        formData.append('image', data.image)
      }

      setLoader(true)
      TeamService.editTeam(formData)
        .then((res) => {
          if (res.status === 200) {
            setErrorMessages([])
            fetchUsers()
            ToastComponent(res.message, 'success')
            onClose()
          } else {
            const errors =
              res.errors && Array.isArray(res.errors)
                ? res.errors
                : [res.message || 'Something went wrong.']
            setErrorMessages(errors)
          }
          setLoader(false)
        })
        .catch(() => {
          ToastComponent('Something went wrong. Please try again.', 'error')
          setLoader(false)
        })
    },
  })

  const handleClose = () => {
    setErrorMessages([])
    onClose()
  }

  return (
    <COffcanvas
      placement="end"
      visible={visible}
      onHide={handleClose}
      backdrop
      style={{ width: 640 }}
    >
      <COffcanvasHeader>
        <div>
          <COffcanvasTitle>Edit Team</COffcanvasTitle>
          <div className="text-medium-emphasis small">Update team details, sponsor and logo.</div>
        </div>
        <CCloseButton className="text-reset" onClick={handleClose} />
      </COffcanvasHeader>
      <COffcanvasBody className="d-flex flex-column">
        {errorMessages.length > 0 && (
          <div className="error-list">
            {errorMessages.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CForm className="row g-3 flex-grow-1" onSubmit={formik.handleSubmit}>
          <CCol xs={6}>
            <CFormLabel htmlFor="name">Team Name *</CFormLabel>
            <CFormInput
              className={
                'form-control' + (formik.errors.name && formik.touched.name ? ' is-invalid' : '')
              }
              id="name"
              placeholder="Team Name"
              defaultValue={teamDetail?.name}
              onChange={formik.handleChange}
            />
            {formik.errors.name && formik.touched.name && (
              <CFormFeedback invalid>{formik.errors.name}</CFormFeedback>
            )}
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="grade_name">Comp This Team Plays In *</CFormLabel>
            <CFormSelect
              aria-label="Select Comp"
              name="grade_name"
              value={gradeName}
              onChange={(e) => setGradeName(e.target.value)}
              id="grade_name"
            >
              <option value="">Select Comp</option>
              {gradeList &&
                gradeList.map((item, key) => (
                  <option value={item?.id} key={key}>
                    {item?.grade} ({item?.sn})
                  </option>
                ))}
            </CFormSelect>
          </CCol>

          <CCol xs={12}>
            <hr className="mt-1 mb-0" />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="sponsor_link">Team Sponsor Link</CFormLabel>
            <CFormInput
              placeholder="Team Sponsor Link"
              defaultValue={teamDetail?.sponsor_link}
              onChange={formik.handleChange}
              aria-label="sponsor_link"
              id="sponsor_link"
            />
          </CCol>
          <CCol xs={6}>
            <CFormLabel htmlFor="team_owner">Team Owner</CFormLabel>
            <CFormInput
              placeholder="Team Owner"
              defaultValue={teamDetail?.team_owner}
              onChange={formik.handleChange}
              aria-label="team_owner"
              id="team_owner"
            />
          </CCol>
          <CCol xs={12}>
            <CFormLabel>Team Sponsor Logo</CFormLabel>
            <ImageDropzone
              minHeight={120}
              file={formik.values.image}
              existingUrl={existingLogoUrl}
              error={formik.touched.image ? formik.errors.image : null}
              onChange={(selected) => {
                formik.setTouched({ ...formik.touched, image: true })
                formik.setFieldValue('image', selected)
              }}
            />
          </CCol>
          <CCol xs={12}>
            <CFormLabel>Owner Details</CFormLabel>
            <ReactQuill theme="snow" ref={quillRef} value={ownerDetail} onChange={setOwnerDetail} />
          </CCol>
        </CForm>
      </COffcanvasBody>
      <div className="d-flex justify-content-between gap-2 p-3 border-top">
        <CButton color="secondary" variant="outline" onClick={handleClose}>
          Cancel
        </CButton>
        <CLoadingButton color="primary" loading={loader} onClick={() => formik.handleSubmit()}>
          Save Changes
        </CLoadingButton>
      </div>
    </COffcanvas>
  )
}

export default EditForm
